




































































import {Component, Vue} from 'vue-property-decorator';
import {httpClient} from '@/api/baseApi';
import Loading from '@c/controls/Loading.vue';
import {EventName} from '@/views/dashboard/enums';
import {
  getErrorOccurredMessage,
  getDeviceChangedMessage,
  getBoundaryPassedMessage,
} from '@/views/dashboard/ProblemLogMessages';

/*
    Элемент общего журнала (/api/logs/common). Структура ответа как и у logs/error:
    [rows, fields]. Ожидаемый набор полей (от сервера):
      [timestamp, uuid, module_type, event_name, level, code, message(JSON-строка)]
    message — JSON-строка; при отрисовке колонки пробуем распарсить её и передать в
    готовые обработчики из ProblemLogMessages (device_changed / error_occurred /
    boundary_passed). Если парсинг/обработка не применима — показываем как есть.
*/
interface CommonItem {
  [key: string]: any;
}

@Component({components: {Loading}})
export default class LogsCommon extends Vue {
  items: CommonItem[] = [];
  headers: any[] = [];

  loading = true;
  error = false;
  page = 1;
  itemsPerPage = 10;

  filterStartDate = '';
  filterEndDate = '';
  filterMinLevel: number | '' = '';
  filterModuleType = '';
  filterEventType = '';

  moduleOptions: {text: string; value: string}[] = [{text: 'Все', value: ''}];

  eventOptions = [
    {text: 'Все', value: ''},
    {text: 'Только границы', value: 'boundary_passed'},
    {text: 'Только устройства', value: 'device_changed'},
    {text: 'Только ошибки', value: 'error_occurred'},
  ];

  levelOptions = [
    {text: 'Все', value: ''},
    {text: '1 и выше', value: 1},
    {text: '2 и выше', value: 2},
    {text: '3 и выше', value: 3},
  ];

  async created() {
    this.loadModuleTypes();
    this.fetch();
  }

  async loadModuleTypes() {
    try {
      const {data} = await httpClient.get<[]>('module_types/');
      const opts = data.map((x: any) => ({text: x.description, value: x.value}));
      this.moduleOptions = [{text: 'Все', value: ''}].concat(opts);
    } catch {
      //Не критично: фильтр по модулю просто останется с опцией «Все».
    }
  }

  buildParams() {
    //Клиентская пагинация: грузим один большой набор (лимит 1000), v-data-table сам листает.
    //Сервер не возвращает total — серверная пагинация невозможна.
    const params: any = {limit: 1000};
    if (this.filterStartDate) {
      params.start_date = new Date(this.filterStartDate).getTime();
    }
    if (this.filterEndDate) {
      params.end_date = new Date(this.filterEndDate).getTime() + 24 * 60 * 60 * 1000 - 1;
    }
    if (this.filterMinLevel !== '') {
      params.min_level = this.filterMinLevel;
    }
    if (this.filterModuleType) {
      params.module_type = this.filterModuleType;
    }
    if (this.filterEventType) {
      params.event_type = this.filterEventType;
    }
    return params;
  }

  headerTitle(name: string): string {
    const map: {[k: string]: string} = {
      timestamp: 'Время',
      uuid: 'UUID',
      module_type: 'Модуль',
      event_name: 'Событие',
      level: 'Уровень',
      code: 'Код',
      message: 'Сообщение',
    };
    return map[name] || name;
  }

  async fetch() {
    this.loading = true;
    this.error = false;
    try {
      const {data} = await httpClient.get('/logs/common', {params: this.buildParams()});
      const [rows, fields] = data;
      const names = (fields || []).map((f: any) => f[0]);
      this.headers = names.map((name: string) => {
        const h: any = {text: this.headerTitle(name), value: name};
        if (name === 'level') h.width = '90px';
        return h;
      });
      this.items = (rows || []).map((row: any) => {
        const item: CommonItem = {};
        names.forEach((name: string, i: number) => {
          item[name] = row[i];
        });
        //message — JSON-строка: пробуем распарсить в .content, чтобы хелперы ProblemLog*
        //могли достать нужные поля (content.mac / content.boundary_id и т.п.).
        if (typeof item.message === 'string') {
          try {
            item.content = JSON.parse(item.message);
          } catch {
            //не JSON: используем строку как есть.
          }
        }
        //ProblemLogMessages ожидают поля .eventName / .module / .content / .level / .code.
        item.eventName = item.event_name;
        item.module = item.module_type;
        return item;
      });
      this.page = 1;
      this.loading = false;
    } catch {
      this.loading = false;
      this.error = true;
    }
  }

  applyFilters() {
    this.page = 1;
    this.fetch();
  }

  resetFilters() {
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.filterMinLevel = '';
    this.filterModuleType = '';
    this.filterEventType = '';
    this.page = 1;
    this.fetch();
  }

  formatTs(ts: number | undefined | null): string {
    if (!ts) return '';
    const ms = ts < 1e12 ? ts * 1000 : ts;
    return new Date(ms).toLocaleString('ru-RU');
  }

  getLevelColor(level: any): string {
    if (level == 0) return 'green';
    else if (level == 1) return 'yellow';
    else if (level == 2) return 'orange';
    else if (level == 3) return 'red';
    else return '#ccc';
  }

  getMessage(item: any): string {
    switch (item.event_name) {
      case EventName.DeviceChanged:
        return getDeviceChangedMessage(item);
      case EventName.ErrorOccurred:
        return getErrorOccurredMessage(item);
      case EventName.BoundaryPassed:
        return getBoundaryPassedMessage(item);
      default:
        return item.message ?? item.event_name ?? '';
    }
  }
}
